import React, { useContext } from 'react'
import { NextPage } from 'next'
import Grid from '@mui/material/Grid'
import Container from '@mui/material/Container'

import Link from './Link'

import COLORS from '@/src/colors'
import { MenuItem } from '@/src/types/api-types'
import { useMenus } from '@/lib/hook/useMenus'
import getRoutes from '@/lib/getRoutes'
import SETTINGS from '@/src/settings'
import settings from '@/src/settings'
import Image from 'next/image'
import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import { Box, useTheme } from '@mui/material'
import ProfileContext from '@/components/Context/ProfileContext'
import { urlSiteGroupement } from '@/lib/urlSiteGroupement'

const displaySubLinks: React.FC<MenuItem> = item => {
  const urlFooter = getRoutes({
    route: 'Pages',
    values: { '[slug]': item.href.replace('/', '') },
  })

  return (
    <div key={item.item}>
      <Link href={urlFooter.href} as={urlFooter.as} prefetch={false}>
        {item.item}
      </Link>
    </div>
  )
}

const Footer: NextPage = () => {
  const theme = useTheme()
  const { siteLesOfficinales } = urlSiteGroupement()

  const usergroups = siteLesOfficinales ? [settings.USERGROUPID_OFFICINAL] : []

  const { menus, isLoading } = useMenus(usergroups)
  const { profile } = useContext(ProfileContext)

  if (isLoading) return null

  const mail = profile?.groupAeliaStore
    ? SETTINGS.contact.mail_aeliastore
    : profile?.groupLesOfficinales
      ? SETTINGS.contact.mail_officinales
      : profile?.groupCos
        ? SETTINGS.contact.mail_cos
        : profile?.groupCentraleMediprix
          ? SETTINGS.contact.mail_centraleMediprix
          : profile?.groupPharmacologyStore
            ? SETTINGS.contact.mail_pharmacologyStore
            : profile?.groupCentraleDpharma
              ? SETTINGS.contact.mail_centraleDpharma
              : profile?.groupHelloPharmacie
                ? SETTINGS.contact.mail_helloPharmacie
                : profile?.groupNormandiePharma
                  ? SETTINGS.contact.mail_normandiePharma
                  : profile?.group37Degres
                    ? SETTINGS.contact.mail_37degres
                    : SETTINGS.contact.mail

  const phone = profile?.groupLesOfficinales
    ? SETTINGS.contact.phone_officinales
    : SETTINGS.contact.phone_pharmedistore
  const fb_link = profile?.groupLesOfficinales
    ? SETTINGS.contact.fb_link_officinales
    : SETTINGS.contact.fb_link
  const insta_link = profile?.groupLesOfficinales
    ? SETTINGS.contact.insta_link_officinales
    : SETTINGS.contact.insta_link

  const titleClassName = 'w-full text-[1.125rem] font-extrabold'
  const displayColClassName = 'flex flex-col justify-between h-full'
  const socialListClassName = 'list-none flex p-0 gap-2'
  const paymentIconsClassName = 'flex items-center gap-4'
  const socialIconSx = {
    mr: 1,
    border: `1px solid ${COLORS.border}`,
    p: 1,
    fontSize: 48,
    '&:hover': {
      color: '#ffffff',
      background: theme.palette.primary.main,
    },
  }

  return (
    <Container
      className="m-8"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
      }}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            display: 'flex',
            justifyContent: { sx: 'flex-start', md: 'center' },
          }}
        >
          <Box>
            <div className={displayColClassName}>
              <div>
                <h2 className={titleClassName}>Nous contacter</h2>
                <div>
                  <Link
                    target="_blank"
                    href={`mailto:${mail}`}
                    useClassicLink
                    prefetch={false}
                    style={{ color: theme.palette.text.primary }}
                  >
                    {mail}
                  </Link>
                </div>
                <div>
                  {t({
                    id: 'web_tel_footer',
                  })}
                  {phone}
                </div>
              </div>
            </div>
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            display: 'flex',
            justifyContent: { sx: 'flex-start', md: 'center' },
          }}
        >
          <Box>
            {menus[0] && (
              <>
                <h2 className={titleClassName}>{menus[0].name}</h2>
                {menus[0].items?.map(displaySubLinks)}
                <div
                  onClick={() =>
                    window.open(
                      'https://d3gefafcz1sn03.cloudfront.net/pharmedistore/video/PharmedistoreEnBref.mp4',
                      '_blank'
                    )
                  }
                  style={{ cursor: 'pointer' }}
                >
                  Découvrez nous en bref
                </div>
                <Grid container spacing={2}>
                  <Grid item md={6} xs={12}>
                    <ul className={socialListClassName}>
                      <li>
                        <Link
                          href={fb_link}
                          target="_blank"
                          title="facebook"
                          rel="noopener"
                          useClassicLink
                          prefetch={false}
                        >
                          <FacebookIcon sx={socialIconSx} />
                        </Link>
                      </li>
                      <li>
                        <Link
                          href={insta_link}
                          target="_blank"
                          title="instagram"
                          rel="noopener"
                          useClassicLink
                          prefetch={false}
                        >
                          <InstagramIcon sx={socialIconSx} />
                        </Link>
                      </li>
                    </ul>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            display: 'flex',
            justifyContent: { sx: 'flex-start', md: 'center' },
          }}
        >
          <Box>
            {menus[1] && (
              <>
                <h2 className={titleClassName}>{menus[1].name}</h2>
                {menus[1].items?.map(displaySubLinks)}
              </>
            )}
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            display: 'flex',
            justifyContent: { sx: 'flex-start', md: 'center' },
          }}
        >
          {!profile?.groupAeliaStore && (
            <Box>
              <div>
                <h2 className={titleClassName}>Moyen de paiement</h2>
                <div className={paymentIconsClassName}>
                  <Image src={'/images/amex.svg'} width={32} height={23} alt={'amex'} />
                  <Image src={'/images/visa.svg'} width={32} height={23} alt={'visa'} />
                  <Image src={'/images/mastercard.svg'} width={32} height={23} alt={'mastercard'} />
                  <Image src={'/images/sepa.svg'} width={44} height={23} alt={'sepa'} />
                </div>
              </div>
            </Box>
          )}
        </Grid>
      </Grid>
    </Container>
  )
}

export default React.memo(Footer)
